"use client"

import { useState, useEffect, useContext } from "react"
import { TextField, Button, Box, Typography, Paper, Grid, Switch, Tooltip, alpha, useTheme, Tabs, Tab } from "@mui/material"
import { ErpContext } from "../../context/ErpContext.js"
import Swal from "sweetalert2"
import GestionEntidadAtributo from "../adminComponent/EntidadAtributoComponent.js"

// Icons
import SaveIcon from "@mui/icons-material/Save"
import EditAttributesIcon from "@mui/icons-material/EditAttributes"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import InventoryIcon from "@mui/icons-material/Inventory"
import PeopleIcon from "@mui/icons-material/People"

const GestionItemAtributo = () => {
  const theme = useTheme()
  const { updateItemAtributo, itemAtributo, getItemAtributo, isNavbarCollapsed } = useContext(ErpContext)

  // Estado para el tab activo
  const [tabValue, setTabValue] = useState(0)

  // Estado único para almacenar atributos como un array de objetos
  const [atributos, setAtributos] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Manejar cambio de tab
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // Cargar datos al montar el componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getItemAtributo() // Llamada para obtener los datos iniciales
      } catch (error) {
        console.error("Error al cargar atributos:", error)
        showAlert("Error al cargar los atributos", "error")
      }
    }
    fetchData()
  }, [])

  // Actualizar el estado local cuando se reciban datos del contexto
  useEffect(() => {
    if (itemAtributo && itemAtributo.length > 0) {
      const atributosIniciales = itemAtributo.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion || "",
        eliminado: !attr.eliminado, // Convertimos `eliminado` a `activo`
      }))
      setAtributos(atributosIniciales)
    }
  }, [itemAtributo])

  // Manejar cambios en los inputs dinámicos
  const handleInputChange = (index, field, value) => {
    const nuevosAtributos = [...atributos]
    nuevosAtributos[index][field] = value
    setAtributos(nuevosAtributos)
  }

  const submitItemAtributo = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Transformamos atributos en un array con la estructura esperada
      const dataToSend = atributos.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        eliminado: !attr.eliminado,
      }))

      // Enviar los datos al backend
      await updateItemAtributo({ atributos: dataToSend }) // Enviar como un objeto con clave 'atributos'

      showAlert("Los atributos se actualizaron con éxito", "success")
    } catch (error) {
      console.error("Error al actualizar atributos:", error)
      showAlert("Error al actualizar los atributos", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Función para mostrar una alerta con SweetAlert2
  const showAlert = (message, icon = "success") => {
    Swal.fire({
      position: "bottom-end",
      icon: icon,
      title: message,
      showConfirmButton: false,
      timer: 1500,
    })
  }

  return (
    <Box sx={{ p: 3, backgroundColor: alpha("#5b5b5f", 0.05) }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        {/* Encabezado */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#5b5b5f",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditAttributesIcon sx={{ color: "rgb(244, 177, 14)" }} />
          <Typography variant="h6">Gestión de Atributos</Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: alpha("#5b5b5f", 0.7),
                "&.Mui-selected": {
                  color: "#5b5b5f",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "rgb(244, 177, 14)",
              },
            }}
          >
            <Tab
              icon={<InventoryIcon />}
              iconPosition="start"
              label="Atributos de Productos"
              sx={{ textTransform: "none", fontWeight: 500 }}
            />
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="Atributos de Entidades"
              sx={{ textTransform: "none", fontWeight: 500 }}
            />
          </Tabs>
        </Box>

        {/* Contenido del Tab de Productos */}
        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: alpha("#5b5b5f", 0.05),
              p: 1,
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" color="text.secondary">
              Active o desactive los atributos y personalice sus nombres. Los atributos activos se mostrarán en los
              formularios de productos.
            </Typography>
          </Box>

          <form onSubmit={submitItemAtributo}>
            <Grid container spacing={2}>
              {atributos.map((atributo, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: "1px solid",
                      borderColor: atributo.eliminado ? "rgb(244, 177, 14)" : alpha("#5b5b5f", 0.3),
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      height: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderColor: atributo.eliminado ? "rgb(244, 177, 14)" : alpha("#5b5b5f", 0.5),
                      },
                    }}
                  >
                    <TextField
                      label={`Atributo ${index + 1}`}
                      value={atributo.descripcion}
                      onChange={(e) => handleInputChange(index, "descripcion", e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: atributo.eliminado ? "rgb(244, 177, 14)" : alpha("#5b5b5f", 0.7),
                        }}
                      >
                        {atributo.eliminado ? "Activo" : "Inactivo"}
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Switch
                          checked={atributo.eliminado}
                          onChange={(e) => handleInputChange(index, "eliminado", e.target.checked)}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "rgb(244, 177, 14)",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: alpha("rgb(244, 177, 14)", 0.5),
                            },
                          }}
                        />

                        <Tooltip title={atributo.eliminado ? "Atributo activo" : "Atributo inactivo"}>
                          {atributo.eliminado ? (
                            <CheckCircleIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                          ) : (
                            <CancelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.5) }} />
                          )}
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: 1.5,
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.9),
                  },
                  "&:disabled": {
                    backgroundColor: alpha("#5b5b5f", 0.5),
                    color: 'white',
                  },
                }}
              >
                {isSubmitting ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </Box>
          </form>
          </Box>
        )}

        {/* Contenido del Tab de Entidades */}
        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <GestionEntidadAtributo />
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default GestionItemAtributo

